import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useCalculatedAmounts } from 'src/hooks/useCalculatedAmounts'
import { InvoiceFormInputs } from '../InvoiceEditForm'

const LOCAL_STORAGE_KEY_WITHHOLDING = 'zeyone_invoice_settings_tax_withholding'
const LOCAL_STORAGE_KEY_UNIT = 'zeyone_invoice_settings_tax_unit'

export const getWithholdingSettingFromLocalStorage = () =>
  localStorage.getItem(LOCAL_STORAGE_KEY_WITHHOLDING) as null | '0' | '1'
export const getUnitSettingFromLocalStorage = () =>
  localStorage.getItem(LOCAL_STORAGE_KEY_UNIT) as null | '0' | '1'

const TaxModalHeader: React.FC = ({ children }) => {
  return (
    <Text
      pb="16px"
      px={0}
      fontSize="16px"
      lineHeight={1}
      fontWeight={700}
      borderBottom="1px solid"
      borderColor="gray.200"
    >
      {children}
    </Text>
  )
}

export const TaxSettingModal: React.VFC = () => {
  const { reset, getValues, setValue, watch } =
    useFormContext<InvoiceFormInputs>()
  const { mutateCalculatedAmount } = useCalculatedAmounts(
    watch('items'),
    watch('taxSettings')
  )
  const { isOpen, onClose, onOpen } = useDisclosure()
  const ref = useRef<InvoiceFormInputs['taxSettings']>()

  const onCancel = () => {
    reset({ ...getValues(), taxSettings: { ...ref.current } })
    onClose()
  }
  // この処理ではこのラジオボタンのデータは保存しない
  const onSubmit = () => {
    const { taxSettings } = getValues()
    localStorage.setItem(LOCAL_STORAGE_KEY_WITHHOLDING, taxSettings.withholding)
    localStorage.setItem(LOCAL_STORAGE_KEY_UNIT, taxSettings.unit)
    ref.current = undefined
    mutateCalculatedAmount()
    onClose()
  }

  return (
    <>
      <Button
        onClick={() => {
          // 開いたときの値を保存し、キャンセル時にこの値でリセット
          ref.current = { ...getValues().taxSettings }
          onOpen()
        }}
        w="50px"
        minH="30px"
        h="30px"
        fontSize="13px"
        lineHeight={1}
        ml="16px"
      >
        設定
      </Button>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <TaxModalHeader>① 金額計算の設定</TaxModalHeader>
            <VStack mt="20px" mb="24px" spacing="20px" align="flex-start">
              <RadioGroup
                onChange={(nextValue) =>
                  setValue('taxSettings.tax_display', nextValue === '0' ? 0 : 1)
                }
                value={String(watch('taxSettings.tax_display'))}
              >
                <Text fontWeight={500} fontSize="12px" lineHeight="20px">
                  税別・税込の設定
                </Text>
                <Flex gap="12px" mt="8px">
                  <Radio variant="highlight" value="0">
                    税別計算
                  </Radio>
                  <Radio variant="highlight" value="1">
                    税込計算
                  </Radio>
                </Flex>
              </RadioGroup>
              <RadioGroup
                onChange={(nextValue) =>
                  setValue(
                    'taxSettings.tax_rounding',
                    nextValue === '0' ? 0 : nextValue === '1' ? 1 : 2
                  )
                }
                value={String(watch('taxSettings.tax_rounding'))}
              >
                <Text fontWeight={500} fontSize="12px" lineHeight="20px">
                  消費税 端数処理
                </Text>
                <Flex gap="12px" mt="8px">
                  <Radio variant="highlight" value="0">
                    四捨五入
                  </Radio>
                  <Radio variant="highlight" value="1">
                    切り捨て
                  </Radio>
                  <Radio variant="highlight" value="2">
                    切り上げ
                  </Radio>
                </Flex>
              </RadioGroup>
              <RadioGroup
                onChange={(nextValue) =>
                  setValue(
                    'taxSettings.amount_rounding',
                    nextValue === '0' ? 0 : nextValue === '1' ? 1 : 2
                  )
                }
                value={String(watch('taxSettings.amount_rounding'))}
              >
                <Text fontWeight={500} fontSize="12px" lineHeight="20px">
                  金額（数量×単位） 端数処理
                </Text>
                <Flex gap="12px" mt="8px">
                  <Radio variant="highlight" value="0">
                    四捨五入
                  </Radio>
                  <Radio variant="highlight" value="1">
                    切り捨て
                  </Radio>
                  <Radio variant="highlight" value="2">
                    切り上げ
                  </Radio>
                </Flex>
              </RadioGroup>
            </VStack>
            <TaxModalHeader>② デフォルト表示の設定</TaxModalHeader>
            <VStack mt="20px" spacing="20px" align="flex-start">
              <RadioGroup
                onChange={(nextValue: '0' | '1') =>
                  setValue('taxSettings.withholding', nextValue)
                }
                value={String(watch('taxSettings.withholding'))}
              >
                <Text fontWeight={500} fontSize="12px" lineHeight="20px">
                  項目：源泉徴収税
                </Text>
                <Flex gap="12px" mt="8px">
                  <Radio variant="highlight" value="0">
                    表示しない
                  </Radio>
                  <Radio variant="highlight" value="1">
                    表示する
                  </Radio>
                </Flex>
              </RadioGroup>
              <RadioGroup
                onChange={(nextValue: '0' | '1') =>
                  setValue('taxSettings.unit', nextValue)
                }
                value={String(watch('taxSettings.unit'))}
              >
                <Text fontWeight={500} fontSize="12px" lineHeight="20px">
                  項目：単位
                </Text>
                <Flex gap="12px" mt="8px">
                  <Radio variant="highlight" value="0">
                    表示しない
                  </Radio>
                  <Radio variant="highlight" value="1">
                    表示する
                  </Radio>
                </Flex>
              </RadioGroup>
            </VStack>
          </ModalBody>

          <ModalFooter lineHeight={1}>
            <Button
              variant="outline"
              color="gray.800"
              fontSize="13px"
              mr="12px"
              onClick={onCancel}
            >
              キャンセル
            </Button>
            <Button fontSize="13px" onClick={onSubmit}>
              この内容で設定
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
